/**
 * CookieBanner — GDPR-compliant cookie consent banner.
 *
 * Se afișează o singură dată per browser (localStorage flag).
 * Categorii:
 *  - "necessary" (întotdeauna ON, nu poate fi dezactivat)
 *  - "analytics" (pixel tracking anonim — implicit ON, dar utilizatorul
 *    poate opta pentru OFF)
 *
 * Comportament:
 *  1. Prima vizită → banner apare jos, două butoane: „Accept toate" / „Setări"
 *  2. User apasă „Setări" → dialog cu switch-uri per categorie
 *  3. User apasă „Accept toate" sau „Salvează preferințele" → flag setat în
 *     localStorage ({ decided: true, analytics: bool })
 *  4. Vizite ulterioare → nu se afișează banner
 *
 * În modul „analytics=false" oprim invocarea trackEvent/pageview (verificat prin
 * localStorage flag în helper).
 *
 * Compliance: minim ceea ce cere GDPR + directiva ePrivacy (opt-in explicit).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie, Settings2, ShieldCheck, BarChart3 } from "lucide-react";

const LS_KEY = "onefdp_cookie_consent";

/** Returns null if user hasn't decided yet, otherwise { decided:true, analytics:bool, timestamp }. */
export function readCookieConsent() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.decided === true) return parsed;
    return null;
  } catch {
    return null;
  }
}

/** Returns true if user has explicitly enabled analytics tracking (or hasn't decided → default ON). */
export function isAnalyticsEnabled() {
  const c = readCookieConsent();
  if (!c) return true; // pre-consent: allow (banner will nudge user)
  return c.analytics === true;
}

function saveConsent(analytics) {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        decided: true,
        analytics,
        timestamp: new Date().toISOString(),
      }),
    );
  } catch (_e) {
    // localStorage disabled — best-effort only
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(() => readCookieConsent() === null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent(true);
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent(analytics);
    setSettingsOpen(false);
    setVisible(false);
  };

  const rejectNonEssential = () => {
    saveConsent(false);
    setVisible(false);
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-md border-t border-orange-500/25"
        data-testid="cookie-banner"
        role="dialog"
        aria-labelledby="cookie-banner-title"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="text-orange-400 shrink-0 mt-0.5" size={20} />
            <p
              id="cookie-banner-title"
              className="text-sm text-zinc-300 leading-relaxed"
            >
              <span className="font-semibold text-white">
                Folosim cookie-uri
              </span>{" "}
              pentru a face Platforma să funcționeze corect și pentru a înțelege
              cum e folosită (analiză anonimizată prin pixel tracking). Nu
              folosim cookie-uri de publicitate.{" "}
              <Link
                to="/termeni-si-conditii"
                className="text-orange-400 underline decoration-orange-500/40 underline-offset-2 hover:text-orange-300"
                data-testid="cookie-terms-link"
              >
                Termeni și condiții
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSettingsOpen(true)}
              className="text-zinc-400 hover:text-white text-xs"
              data-testid="cookie-settings-btn"
            >
              <Settings2 size={14} className="mr-1.5" />
              Setări
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={rejectNonEssential}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
              data-testid="cookie-reject-btn"
            >
              Doar necesare
            </Button>
            <Button
              onClick={acceptAll}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
              data-testid="cookie-accept-all-btn"
            >
              Accept toate
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent
          className="bg-zinc-950 border-white/10 text-white max-w-md"
          data-testid="cookie-settings-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-orange-400 flex items-center gap-2">
              <Settings2 size={18} />
              Preferințe cookie-uri
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Alege ce tipuri de cookie-uri vrei să permiți. Poți schimba
              preferințele oricând.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/40 border border-white/5">
              <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-white text-sm">
                    Necesare
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400">
                    Întotdeauna active
                  </span>
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  Aceste cookie-uri sunt esențiale pentru funcționarea
                  Platformei (autentificare, sesiune, securitate). Nu pot fi
                  dezactivate.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900/40 border border-white/5">
              <BarChart3 size={18} className="text-cyan-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-white text-sm">
                    Analiză (pixel tracking anonim)
                  </span>
                  <Switch
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                    data-testid="cookie-analytics-switch"
                  />
                </div>
                <p className="text-xs text-zinc-400 mt-1">
                  Ne ajută să înțelegem cum e folosită Platforma prin
                  contorizarea vizitelor și evenimentelor cheie (IP hash-uit,
                  fără date personale). Le folosim doar intern.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-white/5">
            <Button
              variant="ghost"
              onClick={() => setSettingsOpen(false)}
              className="text-zinc-400"
              data-testid="cookie-settings-cancel"
            >
              Anulează
            </Button>
            <Button
              onClick={saveCustom}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="cookie-settings-save"
            >
              Salvează preferințele
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
